import { TinyColor } from '@ctrl/tinycolor';
import {
  createTheme,
  darken,
  getContrastRatio,
  hslToRgb,
  SimplePaletteColorOptions,
  ThemeOptions,
  TypographyVariantsOptions,
} from '@mui/material';
import { amber, grey, orange } from '@mui/material/colors';

import { PortalTheme, PortalThemeOptions } from './interfaces';

export const createPortalTheme = <T extends ThemeOptions>(
  customTheme: T | undefined,
): PortalTheme => {
  // This extra theme allows refering to colors within the
  // actually returned theme

  // If the contrast of the background against black is larger than white, then we consider it light mode. Defaults to light mode.
  const isLightMode = customTheme?.palette?.background?.default
    ? getContrastRatio('#000000', customTheme?.palette?.background?.default) >
      getContrastRatio('#FFFFFF', customTheme?.palette?.background?.default)
    : true;

  const baseTheme = createTheme(
    {
      typography: {
        fontFamily: (
          customTheme?.typography as TypographyVariantsOptions | undefined
        )?.fontFamily,
        subtitle3: {
          fontSize: '10px',
          lineHeight: '140%',
          fontWeight: 500 /* medium */,
        },
        subtitle4: {
          fontSize: '10px',
          lineHeight: '140%',
        },
      },
      palette: {
        mode: isLightMode ? 'light' : 'dark',
        secondary: {
          main:
            (customTheme?.palette?.secondary as SimplePaletteColorOptions)
              ?.main ||
            hslToRgb(
              `hsl(${new TinyColor((customTheme?.palette?.primary as SimplePaletteColorOptions)?.main || '#1976D2').toHsl().h}, 29%, 93.9%)`,
            ),
          contrastText: '#000000',
        },
        text: {
          primaryAlternativeContrast: 'rgba(255, 255, 255, 0.6)',
          sectionBody: '#9C9C9C',
          secondary: 'rgba(0, 0, 0, 0.54)',
        },
        neutral: {
          light: grey.A100,
          main: grey.A200,
          dark: grey.A700,
          contrastText: 'rgba(0, 0, 0, 0.87)',
        },
        success: isLightMode
          ? {
              light: '#EBF2EB',
              main: '#70BF84',
              dark: '#1B3117',
              contrastText: '#211F1F',
            }
          : {
              light: '#1B3117',
              main: '#70BF84',
              dark: '#EBF2EB',
              contrastText: '#fff',
            },
        warning: {
          light: amber[100],
          main: orange[400],
          dark: amber[900],
          contrastText: '#FFFFFF',
        },
        error: {
          light: '#F8EBEA',
          main: '#C23F38',
          dark: '#431816',
          contrastText: '#211F1F',
        },
        grey: isLightMode
          ? {
              '50': 'rgba(0,0,0,0.02)', // '#fafafa',
              '100': 'rgba(0,0,0,0.04)', //'#f5f5f5',
              '200': 'rgba(0,0,0,0.07)', //'#eeeeee',
              '300': 'rgba(0,0,0,0.12)', //'#e0e0e0',
              '400': 'rgba(0,0,0,0.26)', //'#bdbdbd',
              '500': 'rgba(0,0,0,0.38)', //'#9e9e9e',
              '600': 'rgba(0,0,0,0.54)', //'#757575',
              '700': 'rgba(0,0,0,0.62)', //'#616161',
              '800': 'rgba(0,0,0,0.74)', //'#424242',
              '900': 'rgba(0,0,0,0.87)', //'#212121',
              A100: 'rgba(0,0,0,0.04)', // '#f5f5f5',
              A200: 'rgba(0,0,0,0.07)', // '#eeeeee',
              A400: 'rgba(0,0,0,0.26)', // '#bdbdbd',
              A700: 'rgba(0,0,0,0.62)', // '#616161',
            }
          : {
              '50': 'rgba(255,255,255,0.13)', // '#fafafa',
              '100': 'rgba(255,255,255,0.26)', //'#f5f5f5',
              '200': 'rgba(255,255,255,0.38)', //'#eeeeee',
              '300': 'rgba(255,255,255,0.46)', //'#e0e0e0',
              '400': 'rgba(255,255,255,0.62)', //'#bdbdbd',
              '500': 'rgba(255,255,255,0.74)', //'#9e9e9e',
              '600': 'rgba(255,255,255,0.88)', //'#757575',
              '700': 'rgba(255,255,255,0.93)', //'#616161',
              '800': 'rgba(255,255,255,0.96)', //'#424242',
              '900': 'rgba(255,255,255,0.98)', //'#212121',
              A100: 'rgba(255,255,255,0.26)', // '#f5f5f5',
              A200: 'rgba(255,255,255,0.38)', // '#eeeeee',
              A400: 'rgba(255,255,255,0.62)', // '#bdbdbd',
              A700: 'rgba(255,255,255,0.93)', // '#616161',
            },
        transactionStatus: {
          received: {
            background: '#FEFBF0',
            icon: '#935F06',
          },
          approved: {
            background: '#F0F7FE',
            icon: '#124DA1',
          },
          ready: {
            background: '#F4F8F2',
            icon: '#305030',
          },
          delivered: {
            background: '#F3FCF7',
            icon: '#1D633B',
          },
          cancelled: {
            background: '#FEF6F6',
            icon: '#A51D1D',
          },
          refunded: {
            background: '#FEF9F0',
            icon: '#935F06',
          },
        },
      },
      shape: {
        borderRadius: 6,
      },
      borderRadiusLarge: 16,
      contentMaxWidth: '430px',
      bottomNavbarHeight: '81px',
      boxShadow: '0',
      card: {
        color: '#8E55FF',
        action: 'rgba(255,255,255,0.56)',
        contrastText: 'rgba(255,255,255,1)',
        secondaryContrastText: 'rgba(255,255,255,0.6)',
        style: '1',
      },
      breakpoints: {
        values: {
          xs: 0,
          sm: 480,
          md: 900,
          lg: 1200,
          xl: 1536,
        },
      },
      darkDivider: 'rgba(0, 0, 0, 0.16)',
      page: {
        color: '#FFFFFF',
        action: 'rgba(0, 0, 0, 0.56)',
        contrastText: 'rgba(0, 0, 0, 0.87)',
        secondaryContrastText: 'rgba(0, 0, 0, 0.54)',
      },
      paper: {
        color: '#FFFFFF',
        action: 'rgba(0, 0, 0, 0.56)',
        contrastText: 'rgba(0, 0, 0, 0.87)',
        secondaryContrastText: 'rgba(0, 0, 0, 0.54)',
      },
    } as PortalThemeOptions,
    customTheme ?? {},
  ) as PortalTheme;

  return createTheme(baseTheme, {
    palette: {
      primary: baseTheme.palette.augmentColor({
        color: {
          main: baseTheme?.palette?.primary?.main,
          contrastText: baseTheme?.palette?.primary?.contrastText,
        },
        name: 'primary',
      }),
      secondary: baseTheme.palette.augmentColor({
        color: {
          main: baseTheme?.palette?.secondary?.main,
          contrastText: baseTheme?.palette?.secondary?.contrastText,
        },
        name: 'secondary',
      }),
      background: {
        highlight: `rgb(from ${baseTheme.palette.primary.main} r g b / 8%)`,
      },
      ...(baseTheme.palette.transactionStatus
        ? { transactionStatus: { ...baseTheme.palette.transactionStatus } }
        : {}),
    },
    components: {
      MuiAlert: {
        styleOverrides: {
          standardInfo: {
            backgroundColor: baseTheme.palette.info.light,
            color: baseTheme.palette.info.dark,
          },
          standardError: {
            backgroundColor: baseTheme.palette.error.light,
            color: baseTheme.palette.error.dark,
          },
          standardSuccess: {
            backgroundColor: baseTheme.palette.success.light,
            color: baseTheme.palette.success.dark,
          },
          standardWarning: {
            backgroundColor: baseTheme.palette.warning.light,
            color: baseTheme.palette.warning.dark,
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            gap: '20px',
          },
        },
      },
      MuiListItem: {
        defaultProps: {
          disableGutters: true,
        },
        styleOverrides: {
          root: {
            minHeight: '54px',
            borderRadius: `${baseTheme.shape.borderRadius}px`,

            '& .MuiSkeleton-root': {
              width: '100%',
              height: '54px',
              borderRadius: `${baseTheme.shape.borderRadius}px`,
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: '24px',
            paddingRight: baseTheme.spacing(2),
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            paddingTop: baseTheme.spacing(1),
            paddingBottom: baseTheme.spacing(1),
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
          disableRipple: false,
        },
        styleOverrides: {
          root: {
            display: 'inline-flex',
            justifyContent: 'center',
            alignItems: 'start',
            paddingTop: baseTheme.spacing(1),
            borderRadius: `${baseTheme.shape.borderRadius}px`,
          },
        },
        variants: [
          {
            props: { size: 'large' },
            style: {
              height: '48px',
              paddingTop: '12px',
            },
          },
          {
            props: { variant: 'text' },
            style: {
              textTransform: 'none',
            },
          },
          {
            props: { variant: 'outlined' },
            style: {
              backgroundColor: baseTheme.paper.color,
              color: baseTheme.paper.contrastText,
            },
          },
        ],
      },
      MuiTypography: {
        defaultProps: {
          variantMapping: {
            subtitle3: 'p',
            subtitle4: 'p',
          },
        },
        variants: [
          {
            props: { variant: 'h1' },
            style: { lineHeight: '140%', fontSize: '48px' },
          },
          {
            props: { variant: 'h2' },
            style: { lineHeight: '140%', fontSize: '34px' },
          },
          {
            props: { variant: 'h3' },
            style: { lineHeight: '140%', fontSize: '24px' },
          },
          {
            props: { variant: 'h4' },
            style: {
              lineHeight: '140%',
              fontSize: '20px',
              fontWeight: 500 /* medium */,
            },
          },
          {
            props: { variant: 'h5' },
            style: { lineHeight: '140%' },
          },
          {
            props: { variant: 'h6' },
            style: { lineHeight: '140%' },
          },
          {
            props: { variant: 'subtitle1' },
            style: { lineHeight: '140%', fontWeight: 500 /* medium */ },
          },
          {
            props: { variant: 'subtitle2' },
            style: { lineHeight: '140%' },
          },
          {
            props: { variant: 'subtitle3' },
            style: {
              lineHeight: '140%',
              fontWeight: 500 /* medium */,
            },
          },
          {
            props: { variant: 'subtitle4' },
            style: {
              lineHeight: '140%',
            },
          },
          {
            props: { variant: 'body1' },
            style: { lineHeight: '140%' },
          },
          {
            props: { variant: 'body2' },
            style: { lineHeight: '140%' },
          },
        ],
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: baseTheme.palette.primary.main,
            border: 'none',
            borderBottom: `2px solid ${baseTheme.darkDivider}`,
            borderRadius: `${baseTheme.borderRadiusLarge}px`,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            color: baseTheme.palette.text.primary,
            backgroundColor: baseTheme.palette.background.paper,
            '&.Mui-selected': {
              backgroundColor: baseTheme.palette.background.paper,
              outline: `2px solid ${baseTheme.palette.primary.main}`,
              '&:hover': {
                backgroundColor: baseTheme.palette.background.paper,
              },
            },
          },
        },
      },
      MuiCircularProgress: {
        variants: [
          {
            props: { size: 'large' },
            style: { width: '32px', height: '32px' },
          },
        ],
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            boxShadow: 'none',
            borderBottom: 'none',
            color: baseTheme.palette.text.primary,
            backgroundColor: baseTheme.palette.background.default,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            color: baseTheme.palette.primary.contrastText,
            backgroundColor: baseTheme.palette.primary.main,
            paddingTop: '0',
            textTransform: 'uppercase',
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            paddingTop: baseTheme.spacing(2),
            paddingBottom: baseTheme.spacing(3),
          },
          label: {
            background: 'transparent',

            '&.Mui-selected': {
              // Use default font size even though selected. Otherwise
              // we get the text clipping over the pin icon.
              fontSize: '0.75rem',
            },
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          disableRipple: false,
          disableTouchRipple: true,
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            backgroundColor: baseTheme.palette.background.paper,
            borderRadius: `${baseTheme.shape.borderRadius}px`,
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: `${baseTheme.shape.borderRadius}px`,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            paddingBottom: baseTheme.spacing(2),
            paddingLeft: baseTheme.spacing(2),
            paddingRight: baseTheme.spacing(2),
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            color: baseTheme.palette.text.primary,
            backgroundColor: baseTheme.palette.background.paper,
            border: `1px solid ${baseTheme.palette.divider}`,
          },
        },
      },
      MuiSwitch: {
        defaultProps: {
          disableRipple: true,
          disableFocusRipple: true,
          disableTouchRipple: true,
        },
        styleOverrides: {
          root: {
            width: 52,
            height: 26,
            padding: 0,
            display: 'flex',

            '&.Mui-disabled': {
              opacity: 0.7,
              cursor: 'not-allowed',
            },

            '&:hover:not(:has(.Mui-disabled))': {
              '& .MuiSwitch-switchBase': {
                backgroundColor: 'transparent',
              },

              '& .MuiSwitch-track': {
                backgroundColor: baseTheme.palette.grey[400],
              },

              '&:has(.MuiSwitch-switchBase.Mui-checked)': {
                '& .MuiSwitch-track': {
                  backgroundColor: darken(baseTheme.palette.primary.main, 0.3),
                },
              },
            },

            '&:active': {
              '& .MuiSwitch-thumb': {
                backgroundColor: baseTheme.palette.common.white,
                height: 22,
                width: 22,
                boxShadow: 'none',
              },

              '& .MuiSwitch-switchBase': {
                width: 26,
                paddingLeft: 0,
                transform: 'translateX(0px)',
                boxShadow: 'none',
              },

              '& .MuiSwitch-switchBase.Mui-checked': {
                width: 27,
                paddingLeft: 5,
                transform: 'translateX(23px)',
                backgroundColor: 'transparent',
              },
            },

            '& .MuiSwitch-switchBase': {
              padding: 0,
              paddingLeft: 2,
              transitionDuration: '100ms',

              '&.Mui-focusVisible + .MuiSwitch-track': {
                outline: '2px solid',
                outlineColor: baseTheme.palette.primary.main,
                outlineOffset: 2,
              },

              '&.Mui-checked': {
                transform: 'translateX(23px)',
                color: baseTheme.palette.common.white,
                paddingLeft: 5,

                '& + .MuiSwitch-track': {
                  opacity: 1,
                  backgroundColor: baseTheme.palette.primary.main,
                },

                '& .MuiSwitch-thumb': {
                  backgroundColor: baseTheme.palette.common.white,
                },
              },

              '&.Mui-disabled': {
                opacity: 1,
                cursor: 'not-allowed',

                '& + .MuiSwitch-track': {
                  opacity: 0.5,
                },
              },
            },

            '& .MuiSwitch-thumb': {
              boxShadow: 'none',
              marginTop: 2,
              width: 22,
              height: 22,
              borderRadius: 13,
              backgroundColor: baseTheme.palette.common.white,
            },

            '& .MuiSwitch-track': {
              borderRadius: 26 / 2,
              opacity: 1,
              backgroundColor: baseTheme.palette.grey[300],
              boxSizing: 'border-box',
              transitionDuration: '200ms',
            },
          },
        },
      },
    },
  }) as PortalTheme;
};
